import {gaPageViewThunk, gaEcomThunk} from '@stencil/hotels-ga/src/thunks';
import {gaConfig} from '@stencil/hotels-config';
import {
  detailSelectorFn,
  hasHermesDataBooleanSelector,
  hasStaticDataSelector,
  hotelDetailsSoldOutStatus,
} from '@stencil/hotels-details-reducer/src/selectors';
import {
  fetchDetailsHermesDataThunk,
  fetchDetailsStaticDataThunk,
  fetchHotelPolicyDataThunk,
  fetchSimilarHotelsThunk,
} from '@stencil/hotels-details-reducer/src/thunks';
import {
  markPerformanceEnd,
  markPerformanceStart,
} from '@stencil/hotels-utils/src/performance-utils';
import React, {useCallback, useEffect} from 'react';
import {Helmet} from 'react-helmet';
import {useDispatch, useSelector} from 'react-redux';

import AboutHotel from '../../../stories/components/hotels/DetailsPage/AboutHotel/AboutHotel';
import SEOFaqDetails from '../DetailsPage/components/SEOFaqDetails/SEOFaqDetails';
import GoSafeCardBlock from '../SmartEngage/containers/GoSafeCardBlock';
import SmartEngageApiContainer from '../SmartEngage/containers/SmartEngageApiContainer';
import SearchWidget from '../SRP/containers/SearchWidget';
import SafetyAndHygiene from './components/SafetyAndHygiene/SafetyAndHygiene';
import QnA from '../UGC/components/QnAChunk';
import RnR from '../UGC/components/RNR';
import RoomSelection from './components/RoomSelection/RoomSelectionChunk';
import SimilarHotels from './components/SimilarHotels/SimilarHotelsChunk';
import Amenities from './components/Amenities/Amenities';
import Breadcrumb from './components/Breadcrumb/Breadcrumb';
import HotelDetails from './components/HotelDetails/HotelDetailsMain';
import LocationMap from './components/LocationMap/LocationMap';
import Navigation from './components/Navigation/Navigation';
import Policy from './components/Policy/PolicyChunk';
import PriceNBookingButtonNPersuasion from './components/PriceNBookingButtonNPersuasion/PriceNBookingButtonNPersuasion';
import SocialPersuasion from './components/SocialPersuasion/SocialPersuasion';
import VoyagerStatusHandler from './components/VoyagerStatusHandler/VoyagerStatusHandler';

import GoSafeSlider from './components/GoSafeSlider/GoSafeSlider';
import * as S from './DetailsPage.styles';
import {gaGoogleRTThunk} from '@stencil/hotels-ga/src/thunks';

// COMPONENTS
// import SafetyAndHygiene from './components/SafetyAndHygiene/SafetyAndHygiene';
// ACTIONS

const DetailsPage = () => {
  const dispatch = useDispatch();
  const hasStaticData = useSelector(hasStaticDataSelector);
  const hasHermesData = useSelector(hasHermesDataBooleanSelector);
  const detail = useSelector(detailSelectorFn);
  const isSoldOut = useSelector(hotelDetailsSoldOutStatus);

  const {staticData = {}} = detail;
  const {seo_markup_data: seoData = {}} = staticData;

  // const hotelId = useSelector(hotelIdSelector);

  /**
   * ***********FLOW OF THIS USE EFFECT ****************
   *
   * fetchNeededDetailsData is an async function to colocate all the thunk dispatches together
   * instead of firing them from one inside the other
   *
   * if they are called from one inside the other, it's difficult to track what is happening where and when
   * .
   * In contrast now this is the flow —
   *
   *   fetchDetailsStaticDataThunk dispatch successfully fetches static data and updates hasStaticData,
   *   which then prompts fetchDetailsHermesDataThunk to get dispatched, and on its success
   *   hotel policy is fetched because it relies on both static and hermes data.
   *
   * The first approach and second approach triggers same number of re renders, because hasStaticData
   * and hasHermesData is accessed from redux state.
   */

  const hermesSuccessCallback = useCallback(() => {
    dispatch(
      gaPageViewThunk('details', {}, () => {
        return markPerformanceEnd(
          gaConfig.DetailPageGAEvents.performanceMarkers.start,
          gaConfig.DetailPageGAEvents.performanceMarkers.end,
          gaConfig.DetailPageGAEvents.performanceMarkers.duration,
        );
      }),
    );
    dispatch(gaGoogleRTThunk('details'));

    dispatch(gaEcomThunk(gaConfig.ecommerceEvents.detailsPageLanding));
  }, [dispatch, gaPageViewThunk, markPerformanceEnd]);

  useEffect(() => {
    async function fetchNeededDetailsData() {
      if (!hasStaticData) {
        await dispatch(fetchDetailsStaticDataThunk('desktop'));
      }

      if (hasStaticData && !hasHermesData) {
        markPerformanceStart(gaConfig.DetailPageGAEvents.performanceMarkers.start);
        await dispatch(
          fetchDetailsHermesDataThunk({hermesSuccessCallback, apiVersion: 'v12', flavor: 'v3'}),
        );
      }
    }

    fetchNeededDetailsData();
  }, [hasStaticData, hasHermesData]);

  /** independently fetch data that is needed after static data and hermes */
  useEffect(() => {
    if (hasStaticData && hasHermesData) {
      dispatch(fetchHotelPolicyDataThunk());
      dispatch(fetchSimilarHotelsThunk('v3'));
    }
  }, [hasStaticData, hasHermesData]);

  return (
    <>
      <Helmet title={seoData.title} meta={seoData.meta} link={seoData.link} />
      <S.DetailCardInnerSection itemScope itemType="http://schema.org/Hotel">
        <VoyagerStatusHandler />
        <SearchWidget isDetailsPage />
        <SmartEngageApiContainer page="Details" />
        <Breadcrumb />
        {/* rename to something like HotelNameNPersuasions */}
        <HotelDetails />
        <PriceNBookingButtonNPersuasion />
        <SocialPersuasion />
        <Navigation />
        {isSoldOut && <SimilarHotels />}
        <SafetyAndHygiene />
        <GoSafeSlider />
        <GoSafeCardBlock ContainerWrapper={S.GoSafeCardBlockWrapper} />
        <RoomSelection />
        <Amenities />
        <RnR />
        <QnA />
        <Policy />
        <LocationMap />
        <AboutHotel />
        {!isSoldOut && <SimilarHotels />}

        <SEOFaqDetails />
      </S.DetailCardInnerSection>
    </>
  );
};

export default DetailsPage;
